import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X, ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n";
import { useCart } from "../../contexts/CartContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";
import MegaMenu from "./MegaMenu";
import Brand from "./Brand";

const LangSwitcher = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 text-xs font-label" data-testid="lang-switcher">
      <button data-testid="lang-en" onClick={() => setLang("en")} className={`px-2 py-1 ${lang === "en" ? "text-ink" : "text-ink-muted hover:text-ink"}`}>EN</button>
      <span className="text-ink-muted">/</span>
      <button data-testid="lang-nl" onClick={() => setLang("nl")} className={`px-2 py-1 ${lang === "nl" ? "text-ink" : "text-ink-muted hover:text-ink"}`}>NL</button>
    </div>
  );
};

const Header = () => {
  const { t, lang } = useI18n();
  const { count } = useCart();
  const { items: wish } = useWishlist();
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const primaryNav = settings?.navigation?.primary || [];

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 glass-header" data-testid="site-header">
      <div className="border-b border-black/5 hidden md:block">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-2 flex items-center justify-between text-xs text-ink-muted font-label">
          <div className="flex items-center gap-6">
            <span><ShieldCheck className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />{t("free_ship_nl")}</span>
            <span className="hidden lg:inline">{t("pay_secure")}</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/track" data-testid="top-track-link" className="hover:text-ink">{t("nav_track")}</Link>
            <Link to="/account" data-testid="top-account-link" className="hover:text-ink">{t("nav_account")}</Link>
            <LangSwitcher />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-6">
        <Brand />

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <MegaMenu />
          {primaryNav.map((n, i) => (
            <Link key={i} to={n.href} data-testid={`nav-primary-${i}`} className="hover:text-terracotta transition-colors">
              {lang === "nl" ? (n.label_nl || n.label_en) : (n.label_en || n.label_nl)}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden lg:flex items-center bg-white border border-black/10 px-3 py-2 w-72">
          <Search className="w-4 h-4 text-ink-muted" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="bg-transparent outline-none px-2 text-sm w-full" />
        </form>

        <div className="flex items-center gap-4">
          <Link to="/wishlist" data-testid="header-wishlist" className="relative p-1.5 hover:text-terracotta">
            <Heart className="w-5 h-5" />
            {wish?.length > 0 && <span className="absolute -top-1 -right-1 bg-terracotta text-white text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">{wish.length}</span>}
          </Link>
          <Link to="/cart" data-testid="header-cart" className="relative p-1.5 hover:text-terracotta">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-ink text-white text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
          </Link>
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {user.role === "admin" && <Link to="/admin" data-testid="header-admin-link" className="text-xs font-label hover:text-terracotta">{t("nav_admin")}</Link>}
              <Link to="/account" data-testid="header-account-link" className="text-xs font-label hover:text-terracotta flex items-center gap-1.5"><User className="w-4 h-4" /> {user.name?.split(" ")[0]}</Link>
              <button data-testid="header-logout" onClick={logout} className="text-xs font-label text-ink-muted hover:text-ink">{t("nav_logout")}</button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-login" className="hidden md:inline text-xs font-label hover:text-terracotta">{t("nav_login")}</Link>
          )}
          <button data-testid="header-menu-toggle" onClick={() => setOpen(!open)} className="md:hidden p-1">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3 text-sm">
            <form onSubmit={submitSearch} className="flex items-center bg-bone border border-black/10 px-3 py-2">
              <Search className="w-4 h-4 text-ink-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="bg-transparent outline-none px-2 text-sm w-full" />
            </form>
            <Link onClick={() => setOpen(false)} to="/shop" className="py-1.5">{t("nav_shop")}</Link>
            {primaryNav.map((n, i) => (
              <Link key={i} onClick={() => setOpen(false)} to={n.href} className="py-1.5">
                {lang === "nl" ? (n.label_nl || n.label_en) : (n.label_en || n.label_nl)}
              </Link>
            ))}
            <Link onClick={() => setOpen(false)} to="/track" className="py-1.5">{t("nav_track")}</Link>
            <Link onClick={() => setOpen(false)} to="/wishlist" className="py-1.5">{t("wishlist")}</Link>
            <Link onClick={() => setOpen(false)} to="/account" className="py-1.5">{t("nav_account")}</Link>
            {user?.role === "admin" && <Link onClick={() => setOpen(false)} to="/admin" className="py-1.5">{t("nav_admin")}</Link>}
            {user ? (
              <button onClick={() => { logout(); setOpen(false); }} className="text-left py-1.5">{t("nav_logout")}</button>
            ) : (
              <Link onClick={() => setOpen(false)} to="/login" className="py-1.5">{t("nav_login")}</Link>
            )}
            <div className="pt-2 border-t border-black/5"><LangSwitcher /></div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
